import { useState, useEffect } from "react";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import ExportOptions from "../ExportOptions";
import Pagination from "../Shared/Pagination";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.muktihospital.com/api/appointment";
const API_KEY =
  process.env.REACT_APP_API_KEY ||
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const money = (n) => (Number(n) || 0).toFixed(2);

const AppointmentAccounts = () => {
  const [allAppointments, setAllAppointments] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const [filterType, setFilterType] = useState("daily");
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedMonth, setSelectedMonth] = useState(today.slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(today.slice(0, 4));

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Accounts" },
    { label: "Appointment" },
  ];

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const headers = ["Sl", "Period", "Appointments", "Income (Fees)"];

  const dataMapper = (() => {
    let counter = 0;
    return (r) => {
      counter += 1;
      return [counter, r.label, r.count, money(r.income)].join("\t");
    };
  })();

  // Fetch all appointments once
  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(API_BASE, {
          method: "GET",
          headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
        });
        const data = await res.json();
        const list = Array.isArray(data.appointments) ? data.appointments : [];
        const approved = list.filter(
          (a) => (a.status || "").toLowerCase() === "approved"
        );
        setAllAppointments(approved);
      } catch (err) {
        setError("Unable to connect to the server. Please try again later.");
        console.error("Appointment fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAppointments();
  }, []);

  useEffect(() => {
    const summarize = (items, label) => {
      const income = items.reduce((s, a) => s + (Number(a.consultationFee) || 0), 0);
      return { id: label, label, count: items.length, income };
    };

    const dateOf = (a) => new Date(a.appointmentDate || a.createdAt);

    let result = [];

    if (filterType === "daily") {
      const start = new Date(startDate);
      const end = new Date(endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayStr = new Date(d).toISOString().split("T")[0];
        const dayItems = allAppointments.filter(
          (a) => dateOf(a).toISOString().split("T")[0] === dayStr
        );
        result.push(summarize(dayItems, new Date(dayStr).toLocaleDateString()));
      }
    } else if (filterType === "monthly") {
      const [year] = selectedMonth.split("-");
      for (let m = 1; m <= 12; m++) {
        const monthItems = allAppointments.filter((a) => {
          const dt = dateOf(a);
          return dt.getFullYear() === Number(year) && dt.getMonth() === m - 1;
        });
        const label = new Date(year, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
        result.push(summarize(monthItems, label));
      }
    } else {
      const yearItems = allAppointments.filter(
        (a) => dateOf(a).getFullYear() === Number(selectedYear)
      );
      result.push(summarize(yearItems, selectedYear));
    }

    setRows(result);
    setCurrentPage(1);
  }, [allAppointments, filterType, startDate, endDate, selectedMonth, selectedYear]);

  const totals = rows.reduce(
    (a, r) => ({ count: a.count + r.count, income: a.income + r.income }),
    { count: 0, income: 0 }
  );

  const totalPages = Math.ceil(rows.length / limit) || 1;
  const pagedRows = rows.slice((currentPage - 1) * limit, currentPage * limit);
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  return (
    <div>
      <PageHeading title="Appointment" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
              <h2 className="text-base font-medium text-gray-700">Appointment Accounts Report</h2>
          </div>
          <div className="flex items-center gap-2">
            <label className="label mb-0 shrink-0">Filter By:</label>
            <div className="w-32 shrink-0">
              <DynamicSelect
                options={filterOptions}
                onChange={(option) => setFilterType(option.value)}
                value={filterOptions.find((opt) => opt.value === filterType)}
                isSearchable={false}
                controlBackgroundColor="#f7fafc"
                controlBorderColor="#cbd5e0"
              />
            </div>
            {filterType === "daily" && (
              <>
                <label className="label mb-0 shrink-0">From:</label>
                <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <label className="label mb-0 shrink-0">To:</label>
                <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </>
            )}
            {filterType === "monthly" && (
              <input type="month" className="input-field" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
            )}
            {filterType === "yearly" && (
              <input type="number" className="input-field w-28" value={selectedYear} min="2000" max="2099" onChange={(e) => setSelectedYear(e.target.value)} />
            )}
            <ExportOptions
              data={rows}
              title="Appointment Accounts Report"
              headers={headers}
              dataMapper={dataMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen((p) => !p)}
            />
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Loading report...</p>
          ) : error ? (
            <p className="text-center text-red-500 py-10">{error}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-slate-400" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Total Appointments</p>
                  <p className="text-2xl font-bold text-slate-700 mt-2">{totals.count}</p>
                  <p className="text-xs text-gray-400 mt-1">Approved</p>
                </div>
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-emerald-500" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Total Income (Fees)</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-2">৳ {money(totals.income)}</p>
                  <p className="text-xs text-gray-400 mt-1">Consultation fees</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-[#FBFBFD]">
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Sl</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {filterType === "daily" ? "Date" : filterType === "monthly" ? "Month" : "Year"}
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Appointments</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-green-700 p-3">Income (Fees)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.length > 0 ? (
                      pagedRows.map((r, index) => (
                        <tr key={r.id} className="hover:bg-gray-50 border-b">
                          <td className="text-left font-inter text-sm text-M-heading-color p-3">{(currentPage - 1) * limit + index + 1}</td>
                          <td className="text-left font-inter text-sm text-M-heading-color p-3">{r.label}</td>
                          <td className="text-right font-inter text-sm text-M-heading-color p-3">{r.count}</td>
                          <td className="text-right font-inter text-sm font-semibold text-green-600 p-3">৳ {money(r.income)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="4" className="text-center p-4 text-gray-500">No appointment data found</td></tr>
                    )}

                    {rows.length > 0 && (
                      <tr className="border-t-2 border-M-text-color/30 bg-gray-50 font-bold">
                        <td className="text-right font-inter text-sm text-M-heading-color p-3" colSpan={2}>Total:</td>
                        <td className="text-right font-inter text-sm text-M-heading-color p-3">{totals.count}</td>
                        <td className="text-right font-inter text-sm text-green-700 p-3">৳ {money(totals.income)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={rows.length}
                itemsPerPage={limit}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
        <ToastContainer />
      </div>
    </div>
  );
};

export default AppointmentAccounts;